import { HttpClient } from '@angular/common/http'
import { Injectable, OnDestroy, signal } from '@angular/core'
import { BehaviorSubject, debounceTime, filter, finalize, map, Observable, of, Subject, timer } from 'rxjs'
import { catchError, combineLatestWith, takeUntil, tap } from 'rxjs/operators'

import { environment } from '../../../environments/environment'
import { Bet, CalculationBet } from '../../pages/my-bets/types/my-bets.type'
import { LADDER_RANGE_LIMIT } from '../constants/common.constant'
import { Market, MarketProvider, PredictedBet, Side } from '../types/market.type'
import { MarketResponse } from '../types/response.type'
import { HelperService } from './helper.service'

// Extend Window interface to include gc method
declare global {
	interface Window {
		gc?: () => void
	}
}

const MARKET_URL = environment.marketUrl
const CLEANUP_INTERVAL = 1 * 60 * 1000 // 1 minute
const DEBOUNCE_TIME = 200 // 1 second debounce for bet updates

@Injectable({
	providedIn: 'root',
})
export class BetService implements OnDestroy {
	currentUserOrders$ = new BehaviorSubject<Bet[]>([]) // Holds the current user's pending bets

	predictedBets$ = new BehaviorSubject<Record<string, PredictedBet>>({}) // Holds bet information to be shown as prediction. Key is either market ID or topic

	matchedNonPendingBets$ = new BehaviorSubject<Bet[]>([]) // Bets with status different from PENDING, sizeRemaining = 0 and sizeMatched > 0

	unmatchedNonPendingBets$ = new BehaviorSubject<Bet[]>([]) // Bets with status different from PENDING, sizeRemaining > 0 and sizeMatched = 0

	partiallyMatchedNonPendingBets$ = new BehaviorSubject<Bet[]>([]) // Bets with status different from PENDING, sizeRemaining > 0 and sizeMatched > 0

	partialAndUnmatchedNonPendingBets$ = new BehaviorSubject<Bet[]>([]) // Bets with status different from PENDING, sizeRemaining > 0 and sizeMatched > 0

	lapsedNonPendingBets$ = new BehaviorSubject<Bet[]>([]) // Bets with status different from PENDING, sizeRemaining = 0 and sizeMatched = 0 and sizeLapsed > 0

	matchedBets$ = new BehaviorSubject<Bet[]>([]) // Bets with sizeRemaining = 0 and sizeMatched > 0

	unmatchedBets$ = new BehaviorSubject<Bet[]>([]) // Bets with sizeRemaining > 0 and sizeMatched = 0

	partiallyMatchedBets$ = new BehaviorSubject<Bet[]>([]) // Bets with sizeRemaining > 0 and sizeMatched > 0

	nonPendingBets$ = new BehaviorSubject<Bet[]>([]) // Bets with status different from PENDING

	pendingBets$ = new BehaviorSubject<Bet[]>([]) // Bets with status different from PENDING

	betsMarkets$ = new BehaviorSubject<Record<string, Market>>({}) // Holds the market for each bet. Key is market ID

	isCashingOut = signal<boolean>(false) // A flag to indicate if a cash-out action is in progress or not.

	isPlacingBet = signal<boolean>(false) // A flag to indicate if a bet placement action is in progress or not.

	isFetchingCurrenUserOrders = signal<boolean>(false) // A flag to indicate if fetching the user's bets is in progress or not.

	unmatchedBets: Bet[] = []

	readonly destroy$ = new Subject<void>()

	readonly cleanupTimer$ = timer(CLEANUP_INTERVAL, CLEANUP_INTERVAL)

	constructor(
		private readonly httpClient: HttpClient,
		private readonly helperService: HelperService,
	) {
		// Call current user orders endpoint at least once on a load
		this.fetchCurrentUserOrders().subscribe()

		// Categorize bets
		this.currentUserOrders$.pipe(takeUntil(this.destroy$), debounceTime(DEBOUNCE_TIME)).subscribe({
			next: (bets) => {
				const matchedNonPendingBets: Bet[] = []
				const unmatchedNonPendingBets: Bet[] = []
				const partiallyMatchedNonPendingBets: Bet[] = []
				const partialAndUnmatchedNonPendingBets: Bet[] = []
				const lapsedNonPendingBets: Bet[] = []

				// If SIZE_REMAINING = 0 => Show MATCHED
				// If SIZE_MATCHED = 0 => Show UNMATCHED
				// If SIZE_MATCHED > 0 and SIZE_REMAINING > 0 => Show Partially Matched
				const nonPendingBets: Bet[] = bets.filter((bet) => bet.status !== 'PENDING')
				const pendingBets: Bet[] = bets.filter((bet) => bet.status === 'PENDING')
				nonPendingBets.forEach((bet) => {
					const stakeRemaining = Number(bet.stakeRemaining)
					const stakeMatched = Number(bet.stakeMatched)
					const sizeLapsed = Number(bet.sizeLapsed)

					if (stakeRemaining === 0 && stakeMatched > 0) {
						matchedNonPendingBets.push(bet)
					} else if (stakeMatched === 0 && stakeRemaining > 0 && sizeLapsed === 0) {
						unmatchedNonPendingBets.push(bet)
						partialAndUnmatchedNonPendingBets.push(bet)
					} else if (stakeRemaining > 0 && stakeMatched > 0) {
						// Split partially matched non-pending bet
						const splitBets = this.splitPartiallyMatchedBet(bet)
						matchedNonPendingBets.push(splitBets[0]) // Matched part
						unmatchedNonPendingBets.push(splitBets[1]) // Unmatched part
						partialAndUnmatchedNonPendingBets.push(splitBets[1]) // Add unmatched part to partial+unmatched
					} else if (stakeRemaining === 0 && stakeMatched === 0 && sizeLapsed > 0) {
						lapsedNonPendingBets.push(bet)
					} else if (stakeMatched === 0 && stakeRemaining > 0) {
						if (!this.unmatchedBets.some((existingBet) => existingBet.betId === bet.betId)) {
							this.unmatchedBets.push(bet)
						}
					}
				})

				this.matchedNonPendingBets$.next(matchedNonPendingBets)
				this.unmatchedNonPendingBets$.next(unmatchedNonPendingBets)
				this.partiallyMatchedNonPendingBets$.next(partiallyMatchedNonPendingBets)
				this.partialAndUnmatchedNonPendingBets$.next(partialAndUnmatchedNonPendingBets)
				this.lapsedNonPendingBets$.next(lapsedNonPendingBets)
				this.nonPendingBets$.next(nonPendingBets)
				this.pendingBets$.next(pendingBets)

				const matchedBets: Bet[] = []
				const unmatchedBets: Bet[] = []
				const partiallyMatchedBets: Bet[] = []
				bets.forEach((bet) => {
					if (Number(bet.stakeRemaining) === 0 && Number(bet.stakeMatched) > 0) {
						matchedBets.push(bet)
					} else if (Number(bet.stakeMatched) === 0 && Number(bet.stakeRemaining) > 0) {
						unmatchedBets.push(bet)
					} else if (Number(bet.stakeRemaining) > 0 && Number(bet.stakeMatched) > 0) {
						// Split partially matched bet into matched and unmatched parts
						const splitBets = this.splitPartiallyMatchedBet(bet)
						matchedBets.push(splitBets[0]) // Matched part
						unmatchedBets.push(splitBets[1]) // Unmatched part
					}
				})

				this.matchedBets$.next(matchedBets)
				this.unmatchedBets$.next(unmatchedBets)
				this.partiallyMatchedBets$.next(partiallyMatchedBets)
			},
		})

		// Setup periodic cleanup
		this.cleanupTimer$.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.performMemoryCleanup()
		})
	}

	ngOnDestroy(): void {
		this.destroy$.next()
		this.destroy$.complete()

		// Clear all BehaviorSubjects to free memory
		this.currentUserOrders$.next([])
		this.predictedBets$.next({})
		this.matchedNonPendingBets$.next([])
		this.unmatchedNonPendingBets$.next([])
		this.partiallyMatchedNonPendingBets$.next([])
		this.partialAndUnmatchedNonPendingBets$.next([])
		this.lapsedNonPendingBets$.next([])
		this.matchedBets$.next([])
		this.unmatchedBets$.next([])
		this.partiallyMatchedBets$.next([])
		this.nonPendingBets$.next([])
		this.pendingBets$.next([])
		this.betsMarkets$.next({})
	}

	/**
	 * Perform periodic memory cleanup
	 */
	private performMemoryCleanup() {
		if (typeof window !== 'undefined' && window.gc) {
			try {
				window.gc()
			} catch (error) {
				console.error(error)
				// Silently fail if gc is not available
			}
		}
		console.log('Memory cleanup performed')
	}

	/**
	 * Get markets that are currently in progress for the current user
	 */
	fetchCurrentUserOrders() {
		this.isFetchingCurrenUserOrders.set(true)
		return this.httpClient.get<MarketResponse<Bet[]>>(`${MARKET_URL}/bf/current-user-order`).pipe(
			map((myBetsResponse) => myBetsResponse.data ?? []),
			tap((bets) => {
				this.currentUserOrders$.next(bets)
				this.isCashingOut.set(false)
				this.isPlacingBet.set(false)
			}),
			finalize(() => {
				this.isFetchingCurrenUserOrders.set(false)
				this.isPlacingBet.set(false)
				this.isCashingOut.set(false)
			}),
		)
	}

	/**
	 * Get bet status
	 */
	fetchBetStatus(params: { customerOrderRef: string }) {
		this.isFetchingCurrenUserOrders.set(true)
		return this.httpClient.get<MarketResponse<Bet>>(`${MARKET_URL}/bf/current-user-order/${params?.customerOrderRef}`).pipe(
			map((myBetsResponse) => myBetsResponse.data),
			finalize(() => {
				this.isFetchingCurrenUserOrders.set(false)
				this.isPlacingBet.set(false)
				this.isCashingOut.set(false)
			}),
			catchError((error) => {
				console.error('Error fetching bet status:', error)
				return of(null)
			}),
		)
	}

	/**
	 * Filters a list of bets to only include those that belong to a specific market.
	 *
	 * @param {CalculationBet[]} bets - An array of bet objects to filter.
	 * @param {string} marketGroupId - The market group identifier to match against.
	 * @return {CalculationBet[]} An array of bets filtered to include only those within the specified market group.
	 */
	getBetsOnlyWithinMarket(bets: CalculationBet[], marketGroupId: string): CalculationBet[] {
		return bets.filter((bet) => bet.marketGroupId === marketGroupId)
	}

	/**
	 * Transforms a predicted betting object into a calculation betting object by calculating
	 * potential profit and loss based on the provided parameters, while mapping other prediction details.
	 *
	 * @param {PredictedBet} predictedBet - The predicted bet object containing details about the bet, including stake, odds, side, and more.
	 * @return {CalculationBet} The transformed calculation bet object with updated profit, loss, and other mapped betting details.
	 */
	enhancePredictedBetToCalculationBet(predictedBet: PredictedBet): CalculationBet {
		return {
			marketGroupId: predictedBet.marketGroupId,
			marketId: predictedBet.marketId,
			marketName: predictedBet.marketName,
			selectionId: predictedBet.selectionId,
			side: predictedBet.side,
			potentialProfit: this.calculatePotentialProfit(
				Number(predictedBet.stake),
				Number(predictedBet.fOdds),
				predictedBet.side,
				Number(predictedBet.fOdds),
				predictedBet.bettingType,
			),
			potentialLoss: this.calculatePotentialLoss(
				Number(predictedBet.stake),
				Number(predictedBet.fOdds),
				predictedBet.side,
				Number(predictedBet.fOdds),
				predictedBet.bettingType,
			),
			fOdds: predictedBet.fOdds ?? 0,
			dfOdds: predictedBet.dfOdds ?? 0,
			rangeOdd: predictedBet.rangeOdd ?? predictedBet.dfOdds ?? 0,
			betSize: predictedBet.stake ?? 0,
			betId: '-1',
			bettingType: predictedBet.bettingType,
			provider: predictedBet.provider,
			handicap: predictedBet.handicap,
			handicapValues: predictedBet.handicapValues,
			averagePriceMatched: predictedBet.averagePriceMatched,
			runnerName: predictedBet.runnerName,
			stakeMatched: 0,
		}
	}

	/**
	 * Calculates the potential profit based on input parameters.
	 *
	 * @param {number} size - The size of the bet.
	 * @param {number} price - The odds of the bet.
	 * @param {Side} side - The side of the bet, either 'BACK' or 'LAY'.
	 * @param {number} priceMatched - The matched price of the bet.
	 * @param {string} bettingType - The type of betting, such as `ODDS`, `LINE`.
	 * @return {number} The calculated potential profit based on the provided parameters.
	 */
	calculatePotentialProfit(size: number, price: number, side: Side, priceMatched: number, bettingType: string): number {
		// WHEN BETTING_TYPE = 'LINE' THEN size
		if (bettingType === 'LINE') {
			return size
		} else {
			// WHEN bf_size = 0 AND side = 'BACK' THEN round(size * (price - 1), 2)
			if (size === 0 && side === 'BACK') {
				return Number((size * (price - 1)).toFixed(2))
			}
			// WHEN bf_size = 0 AND side = 'LAY' THEN size
			else if (size === 0 && side === 'LAY') {
				return size
			}
			// WHEN bf_size > 0 AND side = 'BACK' THEN round(size  (1 - size_cancelled / NULLIF(bf_size, 0))(CASEWHEN price_matched IS NOT NULL AND price_matched > 0 THEN price_matchedELSE priceEND - 1),2)
			else if (size > 0 && side === 'BACK') {
				return Number((size * ((priceMatched > 0 ? priceMatched : price) - 1)).toFixed(2))
			}
			// WHEN bf_size > 0 AND side = 'LAY' THEN round(size * (1 - size_cancelled / NULLIF(bf_size, 0)), 2)
			else if (size > 0 && side === 'LAY') {
				return Number(size.toFixed(2))
			}
			// ELSE 0
			else {
				return 0
			}
		}
	}

	/**
	 * Calculates the potential financial loss based on the input parameters.
	 *
	 * @param {number} size - The size or amount of the bet.
	 * @param {number} price - The price or odds associated with the bet.
	 * @param {Side} side - The side of the bet (e.g., 'BACK' or 'LAY').
	 * @param {number} sizeCancelled - The part of the bet size that has been cancelled.
	 * @param {number} priceMatched - The price at which the bet has been matched, if applicable.
	 * @param {string} bettingType - The type of betting, e.g., 'LINE' or other types.
	 * @return {number} The potential loss amount, calculated based on the provided input parameters.
	 */
	calculatePotentialLoss(size: number, price: number, side: Side, priceMatched: number, bettingType: string): number {
		// WHEN BETTING_TYPE = 'LINE' THEN size
		if (bettingType === 'LINE') {
			return size
		} else {
			// WHEN bf_size = 0 AND side = 'LAY' THEN round(size * (price - 1), 2)
			if (size === 0 && side === 'LAY') {
				return Number((size * (price - 1)).toFixed(2))
			}
			// WHEN bf_size = 0 AND side = 'BACK' THEN size
			else if (size === 0 && side === 'BACK') {
				return size
			}
			// WHEN bf_size > 0 AND side = 'LAY' THEN round(size  (1 - size_cancelled / NULLIF(bf_size, 0))(CASEWHEN price_matched IS NOT NULL AND price_matched > 0 THEN price_matchedELSE priceEND - 1),2)
			else if (size > 0 && side === 'LAY') {
				return Number((size * ((priceMatched > 0 ? priceMatched : price) - 1)).toFixed(2))
			}
			// WHEN bf_size > 0 AND side = 'BACK' THEN round(size * (1 - size_cancelled / NULLIF(bf_size, 0)), 2)
			else if (size > 0 && side === 'BACK') {
				return Number(size.toFixed(2))
			}
			// ELSE 0
			else {
				return 0
			}
		}
	}

	/**
	 * Splits a partially matched bet into two separate bet objects: one representing the matched portion
	 * and the other representing the unmatched portion.
	 *
	 * @param {Bet} bet - The original bet object that may have a partially matched state.
	 * @return {Array} An array containing two bet objects: the first represents the matched portion of the bet,
	 * and the second represents the unmatched portion of the bet.
	 */
	splitPartiallyMatchedBet(bet: Bet): (Bet & { marketGroupId: string; rangeOdd: number })[] {
		return [
			{
				...bet,
				marketGroupId: this.helperService.generateMarketGroupIdForCalculationBet(bet),
				sizeRemaining: 0,
				potentialProfit: this.calculatePotentialProfit(Number(bet.stakeMatched), Number(bet.fOdds), bet.side, Number(bet.fOdds), bet.bettingType),
				potentialLoss: this.calculatePotentialLoss(Number(bet.stakeMatched), Number(bet.fOdds), bet.side, Number(bet.fOdds), bet.bettingType),
				rangeOdd: bet.averagePriceMatched ?? bet.dfOdds,
			}, // Matched bet
			{
				...bet,
				marketGroupId: this.helperService.generateMarketGroupIdForCalculationBet(bet),
				stakeMatched: 0,
				potentialProfit: this.calculatePotentialProfit(Number(bet.stakeRemaining), Number(bet.fOdds), bet.side, Number(bet.fOdds), bet.bettingType),
				potentialLoss: this.calculatePotentialLoss(Number(bet.stakeRemaining), Number(bet.fOdds), bet.side, Number(bet.fOdds), bet.bettingType),
				rangeOdd: bet.averagePriceMatched ?? bet.dfOdds,
			}, // Unmatched bet
		]
	}

	/**
	 * Calculates the profit or loss for a specific runner based on the given bets and market type.
	 *
	 * @param {CalculationBet[]} bets - An array of bets that contains details such as selection ID, stake, odds, potential profit, and potential loss.
	 * @param {number} selectionId - The unique identifier for the runner whose profit or loss is being calculated.
	 * @param {boolean} isLineMarket - Indicates whether the market is a line market(requiring special handling) or not.
	 * @return {number} The calculated profit or loss for the specified runner.
	 */
	calculateRunnerProfitOrLoss(bets: CalculationBet[], selectionId: number, isLineMarket: boolean): number {
		if (isLineMarket) {
			// This is needed because for Fancy markets, selection ID is what makes them unique. This also works for BF LINE markets
			const betForTheRunner = bets.filter((bet) => bet.selectionId === selectionId)
			return Math.min(
				...this.calculateLadderRangesAndValues(betForTheRunner.map((bet) => ({ ...bet, stake: Number(bet.betSize), rangeOdd: bet.dfOdds }))).values,
			)
		}

		const mySelectionBets = bets.filter((bet) => bet.selectionId === selectionId)
		const otherSelectionsBets = bets.filter((bet) => bet.selectionId !== selectionId)

		if (mySelectionBets.length === 0 && otherSelectionsBets.length === 0) {
			return 0
		}

		const currentSelectionProfits: number[] = []
		mySelectionBets.forEach((bet) => {
			if (bet.side === 'BACK') {
				currentSelectionProfits.push(Number(bet.potentialProfit))
			}
		})
		otherSelectionsBets.forEach((bet) => {
			if (bet.side === 'LAY') {
				currentSelectionProfits.push(Number(bet.potentialProfit))
			}
		})

		const currentSelectionLosses: number[] = []
		mySelectionBets.forEach((bet) => {
			if (bet.side === 'LAY') {
				currentSelectionLosses.push(Number(bet.potentialLoss))
			}
		})
		otherSelectionsBets.forEach((bet) => {
			if (bet.side === 'BACK') {
				currentSelectionLosses.push(Number(bet.potentialLoss))
			}
		})

		const mySelectionProfit = currentSelectionProfits.reduce((prev, current) => prev + current, 0)
		const mySelectionLoss = currentSelectionLosses.reduce((prev, current) => prev + current, 0)

		return mySelectionProfit - mySelectionLoss
	}

	/**
	 * Calculates ladder ranges and their corresponding values based on the provided bets.
	 *
	 * @param {Array<{ fOdds: number; rangeOdd: number; side: Side; stake: number }>} bets - An array of bet objects, where each bet includes:
	 * - `fOdds`: The final odds for the bet.
	 * - `rangeOdd`: The range odd to be used to calculate the ranges.
	 * - `side`: The side of the bet (e.g., back or lay).
	 * - `stake`: The amount staked on the bet.
	 * @return {{ ranges: string[]; values: number[] }} An object containing:
	 * - `ranges`: An array of strings representing the ladder ranges.
	 * - `values`: An array of numbers representing the calculated values for the respective ladder ranges.
	 */
	calculateLadderRangesAndValues(bets: { fOdds: number; rangeOdd: number; side: Side; stake: number }[]): { ranges: string[]; values: number[] } {
		// This will contain both already placed bets plus bet to be placed(if any).
		const ranges: string[] = [] // Ranges to be returned for the table to render
		const values: number[] = [] // Values to be returned for the table to render

		// Sort the bets in ascending order
		const sortedCombinedBets = [...bets]
		sortedCombinedBets.sort((a, b) => a.rangeOdd - b.rangeOdd)

		const minDisplayOdd = sortedCombinedBets[0]?.rangeOdd // The minimum displayOdd in the list of displayOdds
		const maxDisplayOdd = sortedCombinedBets[sortedCombinedBets.length - 1]?.rangeOdd // The maximum displayOdd in the list of displayOdds

		for (let currentOdd = Math.max(0, minDisplayOdd - LADDER_RANGE_LIMIT); currentOdd < maxDisplayOdd + (LADDER_RANGE_LIMIT + 1); currentOdd += 1) {
			ranges.push(Math.floor(currentOdd).toString())
			values.push(
				this.generatePredictedRangeValue(
					sortedCombinedBets,
					Math.floor(currentOdd),
					Math.floor(currentOdd) === currentOdd ? MarketProvider.SB : MarketProvider.BF,
				),
			)
		}

		return {
			ranges,
			values,
		}
	}

	/**
	 * Return the ranges and values to be displayed for the predicted P&L table of LINE and Fancy markets
	 * @param bets
	 */
	calculatePlacedBetsLadder(
		bets: Pick<Bet, 'averagePriceMatched' | 'side' | 'potentialProfit' | 'potentialLoss' | 'provider'>[],
	): { ranges: string[]; values: number[] } | null {
		if (bets.length > 0) {
			// This will contain both already placed bets plus bet to be placed(if any).
			const ranges: string[] = [] // Ranges to be returned for the table to render
			const values: number[] = [] // Values to be returned for the table to render

			// Sort the bets in ascending order
			const sortedBets = [...bets]
			sortedBets.sort((a, b) => a.averagePriceMatched - b.averagePriceMatched)

			const minBet = sortedBets[0]
			const maxBet = sortedBets[sortedBets.length - 1]

			let minDisplayOdd = 0 // The minimum displayOdd in the list of displayOdds
			let maxDisplayOdd = 0 // The maximum displayOdd in the list of displayOdds

			if (minBet.provider === MarketProvider.BF) {
				minDisplayOdd = minBet.side === 'BACK' ? Math.floor(Number(minBet.averagePriceMatched)) : Math.ceil(Number(minBet.averagePriceMatched))
				maxDisplayOdd = maxBet.side === 'BACK' ? Math.floor(Number(maxBet.averagePriceMatched)) : Math.ceil(Number(maxBet.averagePriceMatched))
			} else if (maxBet.provider === MarketProvider.SB) {
				minDisplayOdd = minBet.side === 'BACK' ? Number(minBet.averagePriceMatched) : Number(minBet.averagePriceMatched) - 1
				maxDisplayOdd = maxBet.side === 'BACK' ? Number(maxBet.averagePriceMatched) : Number(maxBet.averagePriceMatched) - 1
			}

			for (let currentOdd = Math.max(0, minDisplayOdd - LADDER_RANGE_LIMIT); currentOdd < maxDisplayOdd + (LADDER_RANGE_LIMIT + 1); currentOdd += 1) {
				ranges.push(currentOdd.toString())
				values.push(this.generatePlacedRangeValue(sortedBets, currentOdd))
			}

			return {
				ranges,
				values,
			}
		}

		return null
	}

	/**
	 *
	 * @param bets
	 * @param marketId
	 */
	getMatchedMarketBets(bets: Bet[], marketId: string) {
		const filteredBets = bets.filter((bet) => bet.marketId === marketId && Number(bet.stakeRemaining) === 0 && Number(bet.stakeMatched) > 0)
		return filteredBets.length > 0 ? filteredBets : null
	}

	/**
	 * Calculates the placed profit and loss value for a specific runner in a BF betting market.
	 *
	 * @param {string} marketId - The unique identifier for the market.
	 * @param {number} selectionId - The unique identifier for the runner.
	 * @param {boolean} isLineMarket - Indicates whether the market is a line market or not.
	 * @return {Observable<{result:number}>} An observable emitting the calculated profit or loss value for the runner.
	 */
	getPlacedProfitAndLossValueForBfRunner(marketId: string, selectionId: number, isLineMarket: boolean): Observable<{ result: number }> {
		return this.matchedNonPendingBets$.pipe(
			map((bets) => bets.filter((bet) => bet.marketId === marketId)),
			filter((bets) => bets.length > 0),
			map((bets) => ({
				result: this.calculateRunnerProfitOrLoss(
					bets.map((bet) => ({
						...bet,
						marketGroupId: this.helperService.generateMarketGroupIdForCalculationBet(bet),
						rangeOdd: bet.averagePriceMatched ?? bet.dfOdds,
					})),
					selectionId,
					isLineMarket,
				),
			})),
		)
	}

	/**
	 * Calculates the placed profit and loss value for a specific bookmaker runner based on the provided parameters.
	 *
	 * @param {string} topic - The topic identifier associated with the bookmaker market and runner.
	 * @param {number} selectionId - The selection ID of the runner for which the profit and loss value is calculated.
	 * @param {boolean} isLineMarket - Indicates whether the market is a line market or not.
	 * @return {Observable<{ result: number }>} An observable containing the calculated profit and loss value.
	 */
	getPlacedProfitAndLossValueForBookmakerRunner(topic: string, selectionId: number, isLineMarket: boolean): Observable<{ result: number }> {
		return this.matchedNonPendingBets$.pipe(
			map((bets) => bets.filter((bet) => topic === bet.topic)),
			filter((bets) => bets.length > 0),
			map((bets) => ({
				result: this.calculateRunnerProfitOrLoss(
					bets.map((bet) => ({
						...bet,
						marketGroupId: this.helperService.generateMarketGroupIdForCalculationBet(bet),
						rangeOdd: bet.averagePriceMatched ?? bet.dfOdds,
					})),
					selectionId,
					isLineMarket,
				),
			})),
		)
	}

	/**
	 * Calculates the predicted profit and loss value for a Betfair runner based on market and betting information.
	 *
	 * @param {string} marketId - The unique identifier of the market.
	 * @param {number} selectionId - The unique identifier of the runner (selection).
	 * @param {boolean} isLineMarket - A boolean flag indicating if the market is a line market.
	 * @return {Observable<{ result: number } | null>} An observable that emits the calculated profit or loss value for the specified runner or null if insufficient data is available.
	 */
	getPredictedProfitAndLossValueForBfRunner(marketId: string, selectionId: number, isLineMarket: boolean): Observable<{ result: number } | null> {
		return this.matchedNonPendingBets$.pipe(
			combineLatestWith(this.predictedBets$),
			map(([nonPendingBets, predictedBets]) => {
				if (Boolean(predictedBets[marketId]) && Boolean(predictedBets[marketId].fOdds) && Boolean(predictedBets[marketId].stake)) {
					const predictedBet = predictedBets[marketId]
					const combinedBets: CalculationBet[] = [
						...nonPendingBets
							.filter((bet) => bet.marketId === marketId)
							.map((bet) => ({
								...bet,
								marketGroupId: this.helperService.generateMarketGroupIdForCalculationBet(bet),
								dfOdds: bet.averagePriceMatched,
								rangeOdd: bet.averagePriceMatched,
							})),
						{
							...predictedBet,
							potentialProfit: this.calculatePotentialProfit(
								Number(predictedBet.stake),
								Number(predictedBet.fOdds),
								predictedBet.side,
								Number(predictedBet.fOdds),
								predictedBet.bettingType,
							),
							potentialLoss: this.calculatePotentialLoss(
								Number(predictedBet.stake),
								Number(predictedBet.fOdds),
								predictedBet.side,
								Number(predictedBet.fOdds),
								predictedBet.bettingType,
							),
							fOdds: predictedBet.fOdds!,
							dfOdds: predictedBet.dfOdds!,
							rangeOdd: predictedBet.rangeOdd ?? predictedBet.dfOdds!,
							betSize: predictedBet.stake!,
							betId: '-1',
						},
					]

					return { result: this.calculateRunnerProfitOrLoss(combinedBets, selectionId, isLineMarket) }
				}

				return null
			}),
		)
	}

	/**
	 * Calculates the profit and loss values for a double chance bet placed on a specified runner in a market.
	 *
	 * @param {string} marketId - The identifier of the market containing the runner.
	 * @param {number} selectionId - The identifier of the runner for which the profit and loss values are calculated.
	 * @return {Observable<number[] | null>} - An observable that emits an array containing the profit and loss values for the BACK and LAY sides,
	 * or null if there are no bets placed on the specified runner.
	 */
	getPlacedDoubleChanceProfitAndLossValue(marketId: string, selectionId: number): Observable<number[] | null> {
		return this.matchedNonPendingBets$.pipe(
			map((matchedNonPendingBets) => {
				const runnerBets = matchedNonPendingBets.filter((bet) => bet.marketId === marketId && bet.selectionId === selectionId)

				// If the runner has no bets, don't show the PnL value.
				if (runnerBets.length === 0) {
					return null
				}

				return [
					runnerBets.reduce((aggregate, bet) => aggregate + (bet.side === 'BACK' ? Number(bet.potentialProfit) : -Number(bet.potentialLoss)), 0),
					runnerBets.reduce((aggregate, bet) => aggregate + (bet.side === 'LAY' ? Number(bet.potentialProfit) : -Number(bet.potentialLoss)), 0),
				]
			}),
		)
	}

	/**
	 * Computes the predicted profit and loss values for a double chance betting scenario
	 * by analyzing matched non-pending bets and predicted bets data within the specified market and selection.
	 *
	 * @param {string} marketId - The unique identifier of the market for which the profit and loss values are calculated.
	 * @param {number} selectionId - The unique identifier of the selection within the market.
	 * @return {Observable<number[] | null>} An observable that emits an array with two numerical values representing the predicted profit
	 * and loss for the given market and selection, or null if no valid predictions or criteria are found.
	 */
	getPredictedDoubleChanceProfitAndLossValue(marketId: string, selectionId: number): Observable<number[] | null> {
		return this.matchedNonPendingBets$.pipe(
			combineLatestWith(this.predictedBets$),

			map(([matchedNonPendingBets, predictedBets]) => {
				if (
					Boolean(predictedBets[marketId]) &&
					predictedBets[marketId].selectionId === selectionId &&
					Boolean(predictedBets[marketId].fOdds) &&
					Boolean(predictedBets[marketId].stake)
				) {
					const predictedBet = predictedBets[marketId]
					const runnerBets = [
						...matchedNonPendingBets.filter((bet) => bet.marketId === marketId && bet.selectionId === selectionId),
						{
							side: predictedBet.side,
							potentialProfit: this.calculatePotentialProfit(
								Number(predictedBet.stake),
								Number(predictedBet.fOdds),
								predictedBet.side,
								Number(predictedBet.fOdds),
								predictedBet.bettingType,
							),
							potentialLoss: this.calculatePotentialLoss(
								Number(predictedBet.stake),
								Number(predictedBet.fOdds),
								predictedBet.side,
								Number(predictedBet.fOdds),
								predictedBet.bettingType,
							),
						},
					]

					return [
						runnerBets.reduce((aggregate, bet) => aggregate + (bet.side === 'BACK' ? Number(bet.potentialProfit) : -Number(bet.potentialLoss)), 0),
						runnerBets.reduce((aggregate, bet) => aggregate + (bet.side === 'LAY' ? Number(bet.potentialProfit) : -Number(bet.potentialLoss)), 0),
					]
				}

				return null
			}),
		)
	}

	/**
	 * Calculates the predicted profit and loss value for a bookmaker runner based on non-pending and predicted bets.
	 *
	 * @param {string} topic - The identifier for the bets topic.
	 * @param {number} selectionId - The selection ID for the specific runner.
	 * @param {boolean} isLineMarket - Indicates if the market is a line market.
	 * @return {Observable<{ result: number } | null>} An observable emitting the calculated predicted profit and loss value for the bookmaker runner, or null if no prediction is possible.
	 */
	getPredictedProfitAndLossValueForBookmakerRunner(topic: string, selectionId: number, isLineMarket: boolean): Observable<{ result: number } | null> {
		return this.matchedNonPendingBets$.pipe(
			combineLatestWith(this.predictedBets$),
			map(([nonPendingBets, predictedBets]) => {
				if (Boolean(predictedBets[topic]) && Boolean(predictedBets[topic].fOdds) && Boolean(predictedBets[topic].stake)) {
					const predictedBet = predictedBets[topic]
					const marketBetsFilteredByProvider = nonPendingBets.filter(
						(bet) => topic === this.helperService.generateTopic(bet.marketId, bet.serviceId ?? '0', bet.providerId ?? '0'),
					)
					const combinedBets: CalculationBet[] = [
						...marketBetsFilteredByProvider.map((bet) => ({
							...bet,
							marketGroupId: this.helperService.generateMarketGroupIdForCalculationBet(bet),
							rangeOdd: bet.averagePriceMatched ?? bet.dfOdds,
							averagePriceMatched: bet.averagePriceMatched,
						})),
						{
							...predictedBet,
							potentialProfit: this.calculatePotentialProfit(
								Number(predictedBet.stake),
								Number(predictedBet.fOdds),
								predictedBet.side,
								Number(predictedBet.fOdds),
								predictedBet.bettingType,
							),
							potentialLoss: this.calculatePotentialLoss(
								Number(predictedBet.stake),
								Number(predictedBet.fOdds),
								predictedBet.side,
								Number(predictedBet.fOdds),
								predictedBet.bettingType,
							),
							fOdds: predictedBet.fOdds!,
							dfOdds: predictedBet.dfOdds!,
							rangeOdd: predictedBet.rangeOdd ?? predictedBet.dfOdds!,
							betSize: predictedBet.stake!,
							betId: '-1',
							averagePriceMatched: predictedBet.averagePriceMatched,
						},
					]

					return { result: this.calculateRunnerProfitOrLoss(combinedBets, selectionId, isLineMarket) }
				}

				return null
			}),
		)
	}

	/**
	 * Generates the predicted range value based on the supplied bets, floored current odds, and market provider.
	 *
	 * @param {Array} bets - Array of bet objects containing `fOdds` (number), `rangeOdd` (number), `side` (Side), and `stake` (number).
	 * @param {number} flooredCurrentOdd - The floored value of the current odds for comparison.
	 * @param {MarketProvider} provider - The market provider used to determine conditions for the range calculation.
	 * @return {number} The calculated predicted range value based on the given bets and conditions.
	 */
	private generatePredictedRangeValue(
		bets: { fOdds: number; rangeOdd: number; side: Side; stake: number }[],
		flooredCurrentOdd: number,
		provider: MarketProvider,
	): number {
		return bets
			.map((bet) => {
				if (
					(provider === MarketProvider.BF && bet.side === 'BACK' && flooredCurrentOdd <= bet.rangeOdd) ||
					(bet.side === 'LAY' && flooredCurrentOdd >= bet.rangeOdd) ||
					(provider === MarketProvider.SB && bet.side === 'BACK' && flooredCurrentOdd < bet.rangeOdd) ||
					(bet.side === 'LAY' && flooredCurrentOdd >= bet.rangeOdd)
				) {
					return bet.side === 'BACK' ? bet.stake : bet.stake * (Number(bet.fOdds) - 1)
				}

				return -(bet.side === 'BACK' ? bet.stake * (Number(bet.fOdds) - 1) : bet.stake)
			})
			.reduce((previousValue, currentValue) => previousValue + currentValue, 0)
	}

	/**
	 * Generates a calculated value based on the placed bets and the current odd.
	 *
	 * @param bets An array of bet objects, each including `averagePriceMatched`, `side`, `potentialProfit`, and `potentialLoss`.
	 * @param currentOdd The current odd against which the bets are evaluated.
	 * @return The aggregated value derived from the potential profit or loss of each bet based on the current odd.
	 */
	private generatePlacedRangeValue(bets: Pick<Bet, 'averagePriceMatched' | 'side' | 'potentialProfit' | 'potentialLoss'>[], currentOdd: number): number {
		return bets
			.map((bet) => {
				if ((bet.side === 'BACK' && currentOdd < bet.averagePriceMatched) || (bet.side === 'LAY' && currentOdd >= Math.ceil(bet.averagePriceMatched))) {
					return Number(bet.potentialProfit)
				}

				return -Number(bet.potentialLoss)
			})
			.reduce((previousValue, currentValue) => previousValue + currentValue, 0)
	}

	// eslint-disable-next-line
	getMarketSelectionIds(betModels: any[]): Map<string, string[]> {
		// identify unique market ids
		// for each market id identify the selection id
		// let marketSelectionIds = new Map(marketId, selectionIds)
		// return (map) -> market id,  array of selection ids

		const marketSelectionIds = new Map<string, string[]>()
		// Get unique market IDs from bets
		const uniqueMarketIds = [...new Set(betModels.map((bet) => bet.market_id))]

		for (const marketId of uniqueMarketIds) {
			// Get all runners from market data
			const market = this.betsMarkets$.getValue()[marketId]
			const runners = market?.runners || []

			// Use all market runners as selections
			const allSelections = runners.map((runner) => runner?.selectionId?.toString())
			marketSelectionIds.set(marketId, allSelections)
		}
		return marketSelectionIds
	}
}
